<template>
  <NuxtLoadingIndicator :height="2" />
  <div id="mainApp" :class="{ 'webview-farmi': isWebViewFarmi() }">
    <NuxtLayout>
      <NuxtPage class="content-router" />
    </NuxtLayout>
  </div>
</template>

<script setup lang="ts">
import { useHead, computed, useRouter, useRoute, watch, setPageLayout } from '#imports'
import { getAppContext } from '@b2ag/micro-front-tools'
import { useStore } from 'vuex'
import { getAppTypeLocale, i18n } from '@b2ag/locale'
import { isWebViewFarmi } from '@b2ag/farmi-app-appro-tools'
import { currentAppBrand, isFarmi } from '@b2ag/app-brands'
import { useCategoriesSlugs } from '@b2ag/search/src/composables/useCategoriesSlugs'
import type { Store } from '@b2ag/store'
import { useCheckNewVersion } from './helpers/useCheckNewVersion'

import { initPartners } from '@b2ag/partner/src/initPartners'
import { provideCategoryDescription } from '@b2ag/marketing/src/application/composables/useCategoryDescription'
import { provideCurrentMembership, useCurrentMembership } from '@b2ag/membership/src/composables/useCurrentMembership'
import { provideMembershipStore } from '@b2ag/membership/src/composables/useMembershipStore'
import { provideShoppingList } from '@b2ag/shopping-list/src/composables/useShoppingList'

useCheckNewVersion()

const appTypeLocale = getAppTypeLocale(currentAppBrand)
const { appFrontType } = getAppContext()

const store = useStore() as Store
const router = useRouter()
const route = useRoute()

const isUserLoaded = computed(() => store.getters.isUserLoaded)

setPageLayout(route.meta?.layout || getAppContext().appFrontType)

if (isFarmi) {
  const { provideAddressReactivitySettings } = await import('@b2ag/soufflet-pricer')
  const { makeAddressReactivitySettings } = await import('@/app/contexts/soufflet-pricer')

  provideAddressReactivitySettings(makeAddressReactivitySettings(store))
}

switch (appFrontType) {
  case 'agri':
    initPartners()
    provideCategoryDescription()
    break
  case 'tech':
    provideCurrentMembership(store)
    provideMembershipStore(store)
    provideShoppingList()

    watch(
      isUserLoaded,
      async () => {
        if (isUserLoaded.value && !store.getters.targetCustomer) {
          await useCurrentMembership().loadCurrentMembership()
        }
        await store.dispatch('addresses/fetchAddresses')
      },
      { immediate: true },
    )
    break
  default:
    break
}

useCategoriesSlugs()

// Nécessaire pour les micro-front en module federation
if (typeof window !== 'undefined') {
  window.globalStore = store
  window.parentRouter = router as any
}

useHead(() => {
  return {
    title: appTypeLocale.metadata.title,
    meta: [{ name: 'apple-mobile-web-app-title', content: currentAppBrand }],
    htmlAttrs: {
      lang: i18n.locale,
    },
  }
})
</script>

<style lang="scss">
@import './assets/global.scss';
</style>
